import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { submitInquiry } from "@/lib/inquiries.functions";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact & general inquiries — European Wig Luxury" },
      { name: "description", content: "Contact European Wig Luxury with any question — sizing, insurance and cranial prosthesis invoices, care, orders, or availability." },
      { property: "og:title", content: "Contact European Wig Luxury" },
      { property: "og:description", content: "We answer every message personally, within one business day." },
      { property: "og:url", content: "https://europeanwigluxury.com/contact" },
      { property: "og:image", content: "https://europeanwigluxury.com/og-image.jpg" },
      { name: "twitter:image", content: "https://europeanwigluxury.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://europeanwigluxury.com/contact" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "European Wig Luxury",
          url: "https://europeanwigluxury.com",
          email: "care@europeanwigluxury.com",
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: "09:00",
            closes: "18:00",
          }],
        }),
      },
    ],
  }),
  component: Contact,
});

function Contact() {
  const [sending, setSending] = useState(false);
  const send = useServerFn(submitInquiry);
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const fd = new FormData(form);
    setSending(true);
    try {
      await send({
        data: {
          type: "contact" as const,
          name: String(fd.get("name") ?? ""),
          email: String(fd.get("email") ?? ""),
          phone: String(fd.get("phone") ?? ""),
          subject: String(fd.get("subject") ?? ""),
          message: String(fd.get("message") ?? ""),
        },
      });
      toast.success("Thank you — we'll be in touch within one business day.");
      form.reset();
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please email care@europeanwigluxury.com.");
    } finally {
      setSending(false);
    }
  };
  return (
    <section className="container-luxe grid gap-16 py-16 md:grid-cols-2 md:py-24">
      <div>
        <p className="eyebrow">We reply personally</p>
        <h1 className="mt-3 font-display text-4xl md:text-5xl">General inquiries</h1>
        <p className="mt-6 text-muted-foreground">
          Questions about sizing, availability, care, an existing order, or insurance reimbursement and
          cranial prosthesis invoices? Send us a note and we'll answer within one business day.
        </p>
        <dl className="mt-10 space-y-4 text-sm">
          <div><dt className="eyebrow">Email</dt><dd className="mt-1">care@europeanwigluxury.com</dd></div>
          <div><dt className="eyebrow">Hours</dt><dd className="mt-1">Mon–Fri, 9am–6pm ET</dd></div>
        </dl>
      </div>
      <form onSubmit={onSubmit} className="space-y-5 rounded-md border border-border/70 bg-card p-6 sm:p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label htmlFor="name">Name</Label><Input id="name" name="name" autoComplete="name" required /></div>
          <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" name="email" type="email" autoComplete="email" required /></div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone number</Label>
          <Input id="phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 123-4567" required />
        </div>
        <div className="space-y-2"><Label htmlFor="subject">Subject <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label><Input id="subject" name="subject" /></div>
        <div className="space-y-2"><Label htmlFor="message">Message</Label><Textarea id="message" name="message" rows={6} required /></div>
        <Button type="submit" size="lg" disabled={sending} className="h-12 w-full">{sending ? "Sending…" : "Send inquiry"}</Button>
        <p className="text-xs text-muted-foreground">We'll never share your details.</p>
      </form>
    </section>
  );
}
